// The Sun's far field (the plasma, its texture and the facular activity,
// sunFarField in shaders/sun.js) baked into cube maps, so the photosphere
// shader reads it with one lookup instead of about 80 noise calls a pixel.
//
// Two cubes: the shader shows the front one while the back one is baked a
// band of rows at a time at one frozen time, spread over BAKE_SECONDS, then
// the shader fades to it over FADE_SECONDS and the two swap. Every bake is
// whole and from a single moment, so there are no seams between bands, and
// the far field moves on by fading between moments minutes apart in the
// field's own clock (SUN_FAR_RATE), which it barely changes across.
//
// RGBA8 in sRGB (the colour is stored over SUN_FAR_SCALE, the facular
// activity linear in alpha), with mipmaps; a WebGL2 cube filters across its
// faces' edges, so there is no seam where they meet.
//
// The cubes are made only once the Sun is near enough to want them. Until
// then the photosphere works the far field out itself (uFarBaked 0), which
// for a disc a few dozen pixels across costs nothing; made at load, the
// first bake (six faces of noise, and its shader) held the frame the viewer
// opened on, with the Sun a dot or out of view.
import * as THREE from 'three';
import { sunFarFieldBakeVertexShader, sunFarFieldBakeFragmentShader, SUN_FAR_RATE } from './shaders/sun.js';
import { compileForSceneAsync } from './gpuwarm.js';

const BAKE_SECONDS = 4;
const FADE_SECONDS = 4;
// The first cube, once its shader is built, in this many bands of rows, one
// a frame: counted in frames, so a slow frame is never followed by a bigger
// band to catch up.
const FIRST_BAKE_FRAMES = 30;

// Shared by every Sun material (the viewer's and the To-Scale lineup's).
export const sunFarFieldUniforms = {
  uFarCurr: { value: null },
  uFarNext: { value: null },
  uFarMix: { value: 0 },
  uFarBaked: { value: 0 },
};

let field = null;
let failed = false;

function makeCube(size) {
  return new THREE.WebGLCubeRenderTarget(size, {
    type: THREE.UnsignedByteType,
    format: THREE.RGBAFormat,
    colorSpace: THREE.SRGBColorSpace,
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter,
    magFilter: THREE.LinearFilter,
    depthBuffer: false,
    stencilBuffer: false,
  });
}

function createField(size) {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      sunTexture: { value: null },
      uFarTime: { value: 0 },
      uFace: { value: 0 },
      uSize: { value: size },
      uTexLod: { value: 0 },
    },
    vertexShader: sunFarFieldBakeVertexShader,
    fragmentShader: sunFarFieldBakeFragmentShader,
    depthTest: false,
    depthWrite: false,
  });
  const scene = new THREE.Scene();
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  quad.frustumCulled = false;
  scene.add(quad);
  return {
    size,
    material,
    scene,
    camera: new THREE.Camera(),
    cubes: [makeCube(size), makeCube(size)],
    front: 0,
    phase: 'bake',
    row: 0,
    phaseTime: 0,
    lastSeconds: null,
    texture: null,
  };
}

function setTexture(texture) {
  if (!texture || field.texture === texture) return;
  field.texture = texture;
  // The bake reads the texture by direction, so its date line must wrap.
  if (texture.wrapS !== THREE.RepeatWrapping) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.needsUpdate = true;
  }
  field.material.uniforms.sunTexture.value = texture;
  const width = texture.image && texture.image.width ? texture.image.width : 4096;
  // Texels per radian over bake texels per radian at a face's centre.
  field.material.uniforms.uTexLod.value = Math.max(0, Math.log2((width / (2 * Math.PI)) / (field.size / (Math.PI / 2))));
}

// Bake rows [first, first + count) of the back cube, in the order face by
// face, bottom row up.
function bakeRows(renderer, target, first, count) {
  const size = field.size;
  const u = field.material.uniforms;
  let row = first;
  const end = Math.min(first + count, size * 6);
  while (row < end) {
    const face = Math.floor(row / size);
    const y0 = row - face * size;
    const rows = Math.min(end - row, size - y0);
    u.uFace.value = face;
    target.scissor.set(0, y0, size, rows);
    target.scissorTest = true;
    renderer.setRenderTarget(target, face);
    renderer.render(field.scene, field.camera);
    row += rows;
  }
  return row;
}

function withRendererState(renderer, fn) {
  const target = renderer.getRenderTarget();
  const face = renderer.getActiveCubeFace();
  const mip = renderer.getActiveMipmapLevel();
  const autoClear = renderer.autoClear;
  const xr = renderer.xr.enabled;
  renderer.autoClear = false;
  renderer.xr.enabled = false;
  try {
    fn();
  } finally {
    renderer.setRenderTarget(target, face, mip);
    renderer.autoClear = autoClear;
    renderer.xr.enabled = xr;
  }
}

// The front cube baked whole at `seconds` and the next cycle's bake begun.
function bakeWhole(renderer, seconds) {
  field.material.uniforms.uFarTime.value = seconds * SUN_FAR_RATE;
  withRendererState(renderer, () => bakeRows(renderer, field.cubes[0], 0, field.size * 6));
  showFirst(seconds);
}

// The first cube (cubes[0]) is baked: shown from now, and the next cycle's
// bake begun.
function showFirst(seconds) {
  field.front = 0;
  sunFarFieldUniforms.uFarCurr.value = field.cubes[0].texture;
  sunFarFieldUniforms.uFarNext.value = field.cubes[0].texture;
  sunFarFieldUniforms.uFarMix.value = 0;
  sunFarFieldUniforms.uFarBaked.value = 1;
  field.phase = 'bake';
  field.row = 0;
  field.phaseTime = 0;
  field.material.uniforms.uFarTime.value = (seconds + BAKE_SECONDS + FADE_SECONDS) * SUN_FAR_RATE;
  field.lastSeconds = seconds;
}

// Per frame. sunTexture: the photosphere's texture; size: cube face size
// (fixed when the cubes are made); active: whether the far field is on
// screen at a size where it shows (the bake and the fade pause otherwise);
// wanted: whether the Sun is near enough for the cubes to be made.
export function updateSunFarField(renderer, seconds, { sunTexture, size = 512, active = true, wanted = active } = {}) {
  if (failed || !renderer || !renderer.capabilities.isWebGL2) return;
  try {
    if (!field) {
      // The video renderer makes the cubes on the first frame and bakes the
      // first whole, as before: it seeds Math.random, which three draws from
      // for every object, so making them later would move other random
      // placements, and it needs the same frames on every run.
      const video = typeof window !== 'undefined' && Boolean(window.__vt);
      if (!wanted && !video) return;
      field = createField(size);
      setTexture(sunTexture);
      if (typeof window !== 'undefined') window.__sunFarField = field;
      if (video) {
        bakeWhole(renderer, seconds);
        return;
      }
      // Otherwise its shader is built off the main thread first, and the
      // cube is baked over the next frames at this moment's time.
      field.phase = 'first';
      field.material.uniforms.uFarTime.value = seconds * SUN_FAR_RATE;
      field.lastSeconds = seconds;
      field.compiling = true;
      const made = field;
      compileForSceneAsync(renderer, field.scene, field.camera, field.scene)
        .catch(() => {})
        .then(() => { made.compiling = false; });
      return;
    }
    setTexture(sunTexture);
    // The video renderer (voidmode-youtube/3dsolarsystem/scripts/video, window.__vt, never on the site)
    // jumps its virtual clock minutes ahead before frame 0, so frame 0's
    // animation time is the same however long the warm-up took. The cycle
    // below moves at most 0.25 s a frame and would still carry the warm-up's
    // length into frame 0, so after such a jump it starts again from a whole
    // bake, as on the first call.
    if (typeof window !== 'undefined' && window.__vt && seconds - field.lastSeconds > 60) {
      bakeWhole(renderer, seconds);
      return;
    }
    const dt = Math.min(Math.max(seconds - field.lastSeconds, 0), 0.25);
    field.lastSeconds = seconds;
    if (field.phase === 'first') {
      if (field.compiling) return;
      const total = field.size * 6;
      const band = Math.ceil(total / FIRST_BAKE_FRAMES);
      withRendererState(renderer, () => { field.row = bakeRows(renderer, field.cubes[0], field.row, band); });
      if (field.row >= total) showFirst(seconds);
      return;
    }
    if (!active || dt <= 0) return;
    field.phaseTime += dt;
    const back = field.cubes[1 - field.front];
    if (field.phase === 'bake') {
      const total = field.size * 6;
      const target = Math.min(total, Math.ceil(total * field.phaseTime / BAKE_SECONDS));
      if (target > field.row) {
        withRendererState(renderer, () => { field.row = bakeRows(renderer, back, field.row, target - field.row); });
      }
      if (field.row >= total) {
        field.phase = 'fade';
        field.phaseTime = 0;
        sunFarFieldUniforms.uFarCurr.value = field.cubes[field.front].texture;
        sunFarFieldUniforms.uFarNext.value = back.texture;
        sunFarFieldUniforms.uFarMix.value = 0;
      }
    } else {
      const k = Math.min(field.phaseTime / FADE_SECONDS, 1);
      sunFarFieldUniforms.uFarMix.value = k * k * (3 - 2 * k);
      if (k >= 1) {
        field.front = 1 - field.front;
        sunFarFieldUniforms.uFarCurr.value = field.cubes[field.front].texture;
        sunFarFieldUniforms.uFarNext.value = field.cubes[field.front].texture;
        sunFarFieldUniforms.uFarMix.value = 0;
        field.phase = 'bake';
        field.phaseTime = 0;
        field.row = 0;
        // The next bake is the moment the fade after it will end on.
        field.material.uniforms.uFarTime.value = (seconds + BAKE_SECONDS + FADE_SECONDS) * SUN_FAR_RATE;
      }
    }
  } catch (error) {
    console.warn('[sun far field] bake disabled', error);
    failed = true;
    sunFarFieldUniforms.uFarBaked.value = 0;
  }
}
